/**
 * RenewPage — the public, no-login membership renewal page (/renew/:token).
 *
 * A member taps the link in their reminder SMS/email and lands here. They see
 * their standing, renew their current plan in one tap or switch to another, and
 * pay. No auth: the token in the URL is the credential (verified server-side).
 *
 * Why bare fetch and not the app's api service: api attaches the auth token and
 * redirects to login on a 401 — which would hijack the "expired link" screen.
 * This page talks to the PUBLIC /renew endpoints directly. It reuses only
 * api.defaults.baseURL so it hits the same backend.
 *
 * Payment: POST /renew/:token/pay returns a Paystack authorization_url with the
 * callback set back to this page. We redirect there; Paystack returns the
 * member with ?reference, and we poll /status to confirm + show success in one
 * tab — the smoothest mobile flow.
 *
 * Styling follows Direction 3 (deep navy + Eaze green), consistent with the
 * rest of the app, just warmer for a consumer audience.
 */
import { formatMajor } from "@/lib/currency";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  CalendarClock,
  ArrowRight,
} from "lucide-react";
import api from "@/services/api";
import RenewDetailsEditor from "@/components/RenewDetailsEditor";
import EazeLeadCard from "@/components/EazeLeadCard";

const API_BASE = api.defaults.baseURL || "";

type Phase =
  | "loading"
  | "ready"
  | "redirecting"
  | "confirming"
  | "success"
  | "failed"
  | "expired"
  | "invalid"
  | "error";

interface Plan {
  _id: string;
  name: string;
  price: number;
  validityDays: number;
}
interface ResolveData {
  business: { name: string };
  member: {
    name: string;
    currentPlan: string | null;
    validUntil: string | null;
    status: "active" | "expiring" | "expired" | "none";
  };
  plans: Plan[];
}

const STATUS_PILL: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  expiring: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-700",
  none: "bg-slate-100 text-slate-600",
};
const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  expiring: "Expiring soon",
  expired: "Expired",
  none: "No membership",
};

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const RenewPage: React.FC = () => {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const returnedRef =
    searchParams.get("reference") || searchParams.get("trxref");

  const [phase, setPhase] = useState<Phase>("loading");
  const [data, setData] = useState<ResolveData | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [appliedUntil, setAppliedUntil] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [editingDetails, setEditingDetails] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Resolve token → member + plans ──────────────────────────────
  const resolve = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/renew/${token}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = json?.code;
        if (code === "RENEW_LINK_EXPIRED") return setPhase("expired");
        if (code === "RENEW_LINK_INVALID") return setPhase("invalid");
        setErrorMsg(json?.message || "Something went wrong.");
        return setPhase("error");
      }
      const d = json.data as ResolveData;
      setData(d);
      // Pre-select the member's current plan if it's still in the catalog,
      // else the first plan.
      const match = d.plans.find((p) => p.name === d.member.currentPlan);
      setSelectedId(match?._id || d.plans[0]?._id || "");
      setPhase("ready");
    } catch {
      setErrorMsg("Couldn’t reach the server. Check your connection.");
      setPhase("error");
    }
  }, [token]);

  // ── Poll a payment to completion ────────────────────────────────
  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const pollStatus = useCallback(
    (reference: string) => {
      setPhase("confirming");
      const tick = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/renew/${token}/status/${reference}`,
          );
          const json = await res.json().catch(() => ({}));
          const s = json?.data?.status;
          if (s === "success") {
            stopPolling();
            setAppliedUntil(json.data.appliedValidUntil || null);
            setPhase("success");
          } else if (s === "failed" || s === "abandoned") {
            stopPolling();
            setPhase("failed");
          }
        } catch {
          /* transient — keep polling */
        }
      };
      tick();
      pollTimer.current = setInterval(tick, 4000);
    },
    [token],
  );

  useEffect(() => {
    if (returnedRef) {
      pollStatus(returnedRef); // came back from Paystack
    } else {
      resolve();
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Pay ─────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!selectedId) return;
    setPhase("redirecting");
    try {
      const res = await fetch(`${API_BASE}/renew/${token}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planProductId: selectedId }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.data?.authorizationUrl) {
        setErrorMsg(json?.message || "Couldn’t start the payment.");
        setPhase("ready");
        return;
      }
      // Redirect to Paystack; it returns to this page with ?reference.
      window.location.href = json.data.authorizationUrl;
    } catch {
      setErrorMsg("Couldn’t start the payment. Try again.");
      setPhase("ready");
    }
  };

  // ── Shell ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0A2540] px-4 py-8 sm:py-14">
      <div className="mx-auto w-full max-w-md">
        {/* Brand */}
        <div className="mb-6 text-center">
          <span className="text-lg font-semibold tracking-tight text-white">
            {data?.business.name || "Membership"}
          </span>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-xl sm:p-6">
          {phase === "loading" && (
            <Centered>
              <Loader2 className="h-6 w-6 animate-spin text-[#4CAF50]" />
              <p className="text-sm text-slate-500">Loading…</p>
            </Centered>
          )}

          {(phase === "confirming" || phase === "redirecting") && (
            <Centered>
              <Loader2 className="h-7 w-7 animate-spin text-[#4CAF50]" />
              <p className="text-sm font-medium text-slate-700">
                {phase === "redirecting"
                  ? "Taking you to payment…"
                  : "Confirming your payment…"}
              </p>
              <p className="text-xs text-slate-400">
                This only takes a moment.
              </p>
            </Centered>
          )}

          {phase === "ready" && data && (
            <>
              <h1 className="text-xl font-semibold text-slate-900">
                Hi {data.member.name.split(" ")[0]}, renew your membership
              </h1>
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    STATUS_PILL[data.member.status]
                  }`}
                >
                  {STATUS_LABEL[data.member.status]}
                </span>
                {data.member.validUntil && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <CalendarClock size={12} />
                    {data.member.status === "expired"
                      ? "Lapsed"
                      : "Valid till"}{" "}
                    {fmtDate(data.member.validUntil)}
                  </span>
                )}
              </div>

              {errorMsg && (
                <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {errorMsg}
                </div>
              )}

              {/* Plans */}
              <div className="mt-5 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Choose a plan
                </p>
                {data.plans.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                    No plans available right now. Please renew at the front
                    desk.
                  </p>
                ) : (
                  data.plans.map((p) => {
                    const selected = p._id === selectedId;
                    const isCurrent = p.name === data.member.currentPlan;
                    return (
                      <button
                        key={p._id}
                        type="button"
                        onClick={() => setSelectedId(p._id)}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                          selected
                            ? "border-[#4CAF50] bg-[#4CAF50]/5 ring-1 ring-[#4CAF50]"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">
                              {p.name}
                            </span>
                            {isCurrent && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                Your plan
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">
                            {p.validityDays} days
                          </span>
                        </div>
                        <span className="text-base font-bold tabular-nums text-slate-900">
                          {formatMajor(p.price, (p as { currency?: string }).currency)}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {data.plans.length > 0 && (
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={!selectedId}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4CAF50] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#45A049] disabled:opacity-50"
                >
                  Pay &amp; renew
                  <ArrowRight size={16} />
                </button>
              )}
              <p className="mt-3 text-center text-xs text-slate-400">
                Secure payment by card or mobile money.
              </p>

              <button
                type="button"
                onClick={() => setEditingDetails(true)}
                className="mt-4 w-full text-center text-xs font-medium text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
              >
                Update contact details
              </button>
            </>
          )}

          {phase === "success" && (
            <Centered>
              <CheckCircle2 className="h-12 w-12 text-[#4CAF50]" />
              <p className="text-lg font-semibold text-slate-900">
                You’re renewed!
              </p>
              {appliedUntil && (
                <p className="text-sm text-slate-500">
                  Valid until{" "}
                  <span className="font-medium text-slate-700">
                    {fmtDate(appliedUntil)}
                  </span>
                </p>
              )}
              {/* Growth moment — progressive lead capture. */}
              <EazeLeadCard token={token} apiBase={API_BASE} />
            </Centered>
          )}

          {phase === "failed" && (
            <Centered>
              <XCircle className="h-11 w-11 text-red-500" />
              <p className="text-base font-semibold text-slate-900">
                Payment didn’t go through
              </p>
              <p className="text-sm text-slate-500">
                No charge was made. You can try again.
              </p>
              <button
                type="button"
                onClick={resolve}
                className="mt-2 rounded-xl bg-[#4CAF50] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#45A049]"
              >
                Try again
              </button>
            </Centered>
          )}

          {(phase === "expired" ||
            phase === "invalid" ||
            phase === "error") && (
            <Centered>
              <ShieldAlert className="h-11 w-11 text-amber-500" />
              <p className="text-base font-semibold text-slate-900">
                {phase === "expired"
                  ? "This link has expired"
                  : phase === "invalid"
                    ? "This link isn’t valid"
                    : "Something went wrong"}
              </p>
              <p className="text-sm text-slate-500">
                {phase === "expired"
                  ? "Renewal links expire for your security. We’ll send a fresh one with your next reminder — or ask the front desk."
                  : phase === "error"
                    ? errorMsg || "Please try again shortly."
                    : "Please use the most recent link from your reminder, or ask the front desk."}
              </p>
            </Centered>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          Powered by Eaze
        </p>
      </div>

      {editingDetails && (
        <RenewDetailsEditor
          token={token}
          apiBase={API_BASE}
          onClose={() => setEditingDetails(false)}
        />
      )}
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    {children}
  </div>
);

export default RenewPage;
